import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { dict } from "@/lib/i18n";

const Ctx = createContext(null);

const DEFAULT_SETTINGS = {
  heading_family: "Caught",
  body_family: "Space Grotesk",
  heading_scale: 1.0,
  body_size: 16,
  heading_weight: 500,
  body_weight: 400,
  letter_spacing: -0.02,
  heading_italic: false,
  heading_uppercase: false,
  container_max: 1280,
  section_padding_y: 48,
  card_padding: 24,
  card_radius: 16,
  border_width: 1.5,
  color_primary: "#670003",
  color_background: "#fff0d2",
  color_ink: "#141414",
  site_title: "Lepo Gete Flores",
};

function familyValue(name) {
  if (name === "Caught") return "'Caught', 'Instrument Serif', serif";
  if (name === "Instrument Serif") return "'Instrument Serif', ui-serif, Georgia, serif";
  if (name === "Space Grotesk") return "'Space Grotesk', system-ui, sans-serif";
  return "system-ui, sans-serif";
}

function settingsToCss(s) {
  return `
:root {
  --cream: ${s.color_background};
  --maroon: ${s.color_primary};
  --ink: ${s.color_ink};
  --heading-family: ${familyValue(s.heading_family)};
  --body-family: ${familyValue(s.body_family)};
  --heading-scale: ${s.heading_scale};
  --body-size: ${s.body_size}px;
  --heading-weight: ${s.heading_weight};
  --body-weight: ${s.body_weight};
  --letter-spacing: ${s.letter_spacing}em;
  --container-max: ${s.container_max}px;
  --section-py: ${s.section_padding_y}px;
  --card-padding: ${s.card_padding}px;
  --card-radius: ${s.card_radius}px;
  --border-width: ${s.border_width}px;
}
html, body, #root { background-color: var(--cream); color: var(--ink); }
body { font-family: var(--body-family); font-size: var(--body-size); font-weight: var(--body-weight); }
h1, h2, h3, h4, .font-display {
  font-family: var(--heading-family);
  font-weight: var(--heading-weight);
  letter-spacing: var(--letter-spacing);
  ${s.heading_italic ? "font-style: italic;" : ""}
  ${s.heading_uppercase ? "text-transform: uppercase;" : ""}
}
h1, .font-display.h1 { font-size: calc(3rem * var(--heading-scale)); }
@media (min-width: 768px) { h1, .font-display.h1 { font-size: calc(3.75rem * var(--heading-scale)); } }
@media (min-width: 1024px) { h1, .font-display.h1 { font-size: calc(4.5rem * var(--heading-scale)); } }
.flat-border { border-width: var(--border-width); border-style: solid; border-color: var(--ink); }
.flat-border-2 { border-width: calc(var(--border-width) + 0.5px); border-style: solid; border-color: var(--ink); }
.flat-btn { border-width: var(--border-width); border-style: solid; border-color: var(--ink); }
`;
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "id");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [settings, setSettings] = useState(() => {
    try { return { ...DEFAULT_SETTINGS, ...(JSON.parse(localStorage.getItem("settings_cache") || "{}")) }; }
    catch { return DEFAULT_SETTINGS; }
  });
  const lastGoodRef = useRef(settings);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setChecking(false); }
    else {
      api.get("/auth/me").then((r) => setUser(r.data))
        .catch(() => { localStorage.removeItem("token"); })
        .finally(() => setChecking(false));
    }
    // Load settings (public)
    api.get("/settings").then((r) => {
      const merged = { ...DEFAULT_SETTINGS, ...r.data };
      setSettings(merged);
      lastGoodRef.current = merged;
      localStorage.setItem("settings_cache", JSON.stringify(merged));
      if (merged.site_title) document.title = merged.site_title;
    }).catch(() => { /* fall back to cached */ });
  }, []);

  // Inject <style> tag whenever settings change
  useEffect(() => {
    let el = document.getElementById("app-theme");
    if (!el) {
      el = document.createElement("style");
      el.id = "app-theme";
      document.head.appendChild(el);
    }
    try {
      el.textContent = settingsToCss(settings);
      if (settings.site_title) document.title = settings.site_title;
      lastGoodRef.current = settings;
      localStorage.setItem("settings_cache", JSON.stringify(settings));
    } catch (e) {
      // Revert on error
      setSettings(lastGoodRef.current);
      // eslint-disable-next-line no-console
      console.error("Theme apply failed, reverted:", e);
    }
  }, [settings]);

  const login = async (username, password) => {
    const r = await api.post("/auth/login", { username, password });
    localStorage.setItem("token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const logout = () => { localStorage.removeItem("token"); setUser(null); };
  const toggleLang = () => setLang((l) => (l === "id" ? "en" : "id"));

  const revertSettings = useCallback(() => {
    setSettings(lastGoodRef.current);
  }, []);

  const applySettings = useCallback(async (patch) => {
    const optimistic = { ...settings, ...patch };
    setSettings(optimistic); // instant preview
    try {
      const r = await api.put("/settings", patch);
      const merged = { ...DEFAULT_SETTINGS, ...r.data };
      setSettings(merged);
      lastGoodRef.current = merged;
      return { ok: true, settings: merged };
    } catch (e) {
      // Backend rejected — revert
      setSettings(lastGoodRef.current);
      return { ok: false, error: e.response?.data?.detail || e.message };
    }
  }, [settings]);

  const resetSettings = useCallback(async () => {
    try {
      const r = await api.post("/settings/reset");
      const merged = { ...DEFAULT_SETTINGS, ...r.data };
      setSettings(merged);
      lastGoodRef.current = merged;
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || e.message };
    }
  }, []);

  const t = useMemo(() => dict[lang], [lang]);

  return (
    <Ctx.Provider value={{ lang, setLang, toggleLang, t, user, login, logout, checking, settings, applySettings, resetSettings, revertSettings, DEFAULT_SETTINGS }}>
      {children}
    </Ctx.Provider>
  );
}

export function useApp() { return useContext(Ctx); }
